import Dexie from 'dexie';
import type { AttemptInput, AttemptRejection, AttemptResult } from '@lk/shared';
import type { LearnKidsDb, QueuedAttempt } from './db';

/** Скільки спроб їде в одному запиті. Контракт дозволяє до 100. */
export const BATCH_SIZE = 25;

/** Стеля затримки між повторами — півхвилини. Далі чекати немає сенсу. */
export const MAX_BACKOFF_MS = 30_000;
const BASE_BACKOFF_MS = 700;

/**
 * Скільки чекати, коли сервер сказав «це не твої відповіді» (403).
 * Пачка не винна: за пристрій просто сів хтось інший. Стукати щосекунди
 * марно, палити рядки — злочин, тож просто відкладаємо до наступного входу.
 */
export const FOREIGN_PARK_MS = 5 * 60_000;

/**
 * Далі цієї межі жодна чесна НАША затримка не сягає (пор. MAX_BACKOFF_MS і
 * FOREIGN_PARK_MS). Якщо `nextAttemptAt` опинився за горизонтом — це не план,
 * а слід переведеного вперед годинника пристрою: дитина «погралася з часом»,
 * а її відповіді через це не дозріють ніколи. Такі рядки лікуємо.
 *
 * Слово «наша» тут ключове: паузу вміє призначати ще й СЕРВЕР (`retryAt` у
 * вироку — «розклад відчиниться о 08:00»), і вона законно буває на години.
 * Такі рядки під це лікування не підпадають — див. `parkedByServer`.
 */
export const MAX_SCHEDULE_HORIZON_MS = 60 * 60_000;

/**
 * Стеля довіри до серверної паузи. `nextScheduleOpening` шукає відкриття
 * щонайбільше на тиждень уперед, тож усе, що далі, — це вже не план сервера,
 * а різниця годинників (наприклад, пристрій перевели назад ПІСЛЯ вироку).
 * Такий рядок лікуємо на загальних підставах, інакше він чекав би роками.
 */
export const MAX_SERVER_PARK_MS = 8 * 24 * 3600_000;

/**
 * Скільки разів поспіль сервер може сказати «поверни цю спробу пізніше»,
 * перш ніж ми перестанемо вірити, що воно колись мине.
 *
 * Стеля потрібна, бо «повторюваний вирок» — це рівно та сама отрута, тільки
 * нескінченно ретраєна: рядок, який щоразу вертається з тією ж відмовою,
 * крутив би чергу довіку, а дитина бачила б вічне «ще перевіряємо». Після
 * стелі рядок іде в `rejected`: видно людині, оборотно кнопкою.
 */
export const MAX_RETRYABLE_VERDICTS = 5;

/** Ключ у `meta`: чия черга зараз активна на цьому пристрої. */
const ACTIVE_LEARNER_KEY = 'queue.activeLearnerId';

/**
 * Експоненційна затримка з «джитером».
 * Джитер потрібен, щоб дві вкладки (чи два браузери) не били по серверу
 * синхронно однаковими пачками після відновлення мережі.
 */
export function computeBackoffMs(tries: number, random: () => number = Math.random): number {
  const exponential = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** Math.max(0, tries - 1));
  const jitter = exponential * 0.25 * random();
  return Math.round(exponential + jitter);
}

/** Хто зараз працює за пристроєм, за даними самої черги. */
export async function getActiveLearner(db: LearnKidsDb): Promise<string | null> {
  const row = await db.meta.get(ACTIVE_LEARNER_KEY);
  return typeof row?.value === 'string' ? row.value : null;
}

export interface ActivateResult {
  /** Чи це справді інша дитина, ніж була до цього. */
  changed: boolean;
  /** Скільки рядків повернулося в роботу через цю зміну. */
  revived: number;
}

/**
 * Оголошує, чия черга активна. Викликається і на вході в застосунок,
 * і на кожній записаній відповіді — власником стає той, чиї відповіді
 * реально лягають у чергу.
 *
 * Зміна дитини — це єдиний момент, коли має сенс дати другий шанс усьому,
 * що сервер колись відхилив: попередня відмова могла бути наслідком того,
 * що пачку відправили під чужою сесією.
 */
export async function activateLearner(
  db: LearnKidsDb,
  learnerId: string,
  now = Date.now(),
): Promise<ActivateResult> {
  return db.transaction('rw', db.attempts, db.meta, async () => {
    const previous = await getActiveLearner(db);
    if (previous === learnerId) return { changed: false, revived: 0 };
    await db.meta.put({ key: ACTIVE_LEARNER_KEY, value: learnerId });
    const revived = await reviveRejected(db, { learnerId, now });
    const healed = await healFutureSchedules(db, now);
    return { changed: true, revived: revived + healed };
  });
}

/**
 * Знімає власника черги.
 *
 * Потрібне при вході батька: у нього немає learnerId, і поки в `meta`
 * лишається остання дитина, черга вважає її рядки «своїми» й везе їх під
 * батьківською сесією. Дані від цього не гинуть (сервер відповість 403),
 * але дитина бачить чужу помилку, а батько — чужий лічильник.
 *
 * Рядки не чіпаємо: вони чекають на повернення своєї дитини.
 */
export async function clearActiveLearner(db: LearnKidsDb): Promise<void> {
  await db.meta.delete(ACTIVE_LEARNER_KEY);
}

/**
 * Успадковує власника з самої черги — для баз, що встигли пожити до того,
 * як черга навчилася питати «чия ти». Беремо найсвіжішу невідправлену
 * відповідь: її дитина й сиділа за пристроєм останньою.
 *
 * Помилка тут майже нічого не коштує (сервер відповість 403, пачка зачекає
 * справжнього власника), а от без цього кроку стара черга завмерла б назавжди.
 */
export async function adoptOwnerFromQueue(
  db: LearnKidsDb,
  now = Date.now(),
): Promise<string | null> {
  return db.transaction('rw', db.attempts, db.meta, async () => {
    const known = await getActiveLearner(db);
    if (known) return known;
    const newest = await db.attempts
      .orderBy('createdAt')
      .reverse()
      .filter((row) => row.status !== 'synced')
      .first();
    if (!newest) return null;
    await activateLearner(db, newest.learnerId, now);
    return newest.learnerId;
  });
}

export interface EnqueueArgs {
  db: LearnKidsDb;
  learnerId: string;
  input: AttemptInput;
  now?: number;
}

/**
 * Кладе спробу в чергу. Використовує `put` за первинним ключем,
 * тому повторний виклик з тим самим `input.id` НЕ створить дубль —
 * це перший з двох рівнів дедуплікації (другий — на сервері).
 *
 * Якщо спроба вже відправлена (synced), її не «воскрешаємо»:
 * повторний запис не має відкотити підтверджений стан.
 */
export async function enqueueAttempt({
  db,
  learnerId,
  input,
  now = Date.now(),
}: EnqueueArgs): Promise<QueuedAttempt> {
  return db.transaction('rw', db.attempts, db.meta, async () => {
    // Той, чиї відповіді лягають у чергу, і є поточним власником черги.
    // Це остання лінія оборони: навіть якщо застосунок забув оголосити зміну
    // дитини на вході, перша ж її відповідь переставить власника — і чужі
    // рядки перестануть потрапляти у вибірку.
    await activateLearner(db, learnerId, now);

    const existing = await db.attempts.get(input.id);
    if (existing && (existing.status === 'synced' || existing.status === 'syncing')) {
      return existing;
    }

    const row: QueuedAttempt = {
      id: input.id,
      learnerId,
      sessionId: input.sessionId,
      itemId: input.itemId,
      payload: input,
      status: 'pending',
      tries: existing?.tries ?? 0,
      nextAttemptAt: now,
      lastError: existing?.lastError ?? null,
      createdAt: existing?.createdAt ?? now,
      syncedAt: null,
      result: existing?.result ?? null,
      rejection: existing?.rejection ?? null,
    };
    await db.attempts.put(row);
    return row;
  });
}

export interface ClaimOptions {
  limit?: number;
  now?: number;
  /**
   * Чию чергу вибирати. Якщо не вказано — беремо активного власника з `meta`
   * (його ставить вхід у застосунок і кожна записана відповідь).
   */
  learnerId?: string;
}

/**
 * Забирає з черги пачку «дозрілих» спроб і одразу помічає їх як `syncing`,
 * щоб паралельний виклик flush не забрав ті самі рядки вдруге.
 * Все в одній транзакції — інакше два таймери можуть перетнутися.
 *
 * Пачка ЗАВЖДИ належить рівно одній дитині. Це не оптимізація, а вимога
 * безпеки: запит іде під кукою того, хто увійшов зараз, тож чужа відповідь
 * у пачці — це або 403 на всю пачку, або (гірше) чужий бал у чужому гаманці.
 * Тому вибірка йде складеним індексом, що починається з learnerId: узяти
 * чуже тут просто ніде.
 */
export async function claimBatch(
  db: LearnKidsDb,
  options: ClaimOptions = {},
): Promise<QueuedAttempt[]> {
  const limit = options.limit ?? BATCH_SIZE;
  const now = options.now ?? Date.now();

  return db.transaction('rw', db.attempts, db.meta, async () => {
    const owner = options.learnerId ?? (await getActiveLearner(db));
    // Власник невідомий — чекаємо, поки застосунок скаже, хто за пристроєм.
    // Слати «щось із черги» навмання не можна: це і є вихідний дефект.
    if (!owner) return [];

    const ripe = await db.attempts
      .where('[learnerId+status+nextAttemptAt]')
      .between([owner, 'pending', Dexie.minKey], [owner, 'pending', now], true, true)
      .limit(limit)
      .toArray();

    if (ripe.length === 0) return [];
    // Порядок відправки — хронологічний: UUIDv7 монотонний, але createdAt надійніший.
    ripe.sort((a, b) => a.createdAt - b.createdAt);
    await db.attempts.bulkPut(ripe.map((row) => ({ ...row, status: 'syncing' as const })));
    return ripe;
  });
}

export interface ApplyOutcomesArgs {
  db: LearnKidsDb;
  /** Усе, що поїхало в пачці. */
  ids: string[];
  /** Прийняті — з авторитетним результатом. */
  results: AttemptResult[];
  /** Не зараховані — з причиною і вказівкою, чи є сенс повторювати. */
  rejected: AttemptRejection[];
  now?: number;
  random?: () => number;
}

export interface OutcomeCounts {
  accepted: number;
  rejected: number;
  /** Скільки рядків повернулося в чергу (оборотна відмова або мовчанка сервера). */
  requeued: number;
}

/**
 * Рознесення відповіді сервера по рядках черги. Саме тут вилікувано дефект
 * «все або нічого».
 *
 * Головна зміна проти старого `markSynced(ids)`: `synced` ставиться ТІЛЬКИ за
 * явним підтвердженням сервера, а не «за замовчуванням від того, що пачка не
 * впала». Раніше всі id пачки ставали `synced`, і спроба без результату
 * тихо зависала як «ще не перевірена» назавжди.
 *
 * Три долі, і всі три — не кінцеві, крім `synced`:
 *  · прийнято          → `synced` + авторитетний результат;
 *  · відмова остаточна → `rejected` + причина (видно людині, оборотно кнопкою);
 *  · відмова оборотна  → назад у `pending`, не раніше за названий сервером час.
 *
 * Рядок, про який сервер не сказав нічого, лишається в черзі — мовчання
 * не є підтвердженням.
 */
export async function applyOutcomes({
  db,
  ids,
  results,
  rejected,
  now = Date.now(),
  random = Math.random,
}: ApplyOutcomesArgs): Promise<OutcomeCounts> {
  const accepted = new Map(results.map((r) => [r.attemptId, r]));
  const refused = new Map(rejected.map((r) => [r.attemptId, r]));
  const counts: OutcomeCounts = { accepted: 0, rejected: 0, requeued: 0 };

  await db.transaction('rw', db.attempts, async () => {
    const rows = await db.attempts.bulkGet(ids);
    const updated: QueuedAttempt[] = [];

    for (const row of rows) {
      if (!row) continue;

      const result = accepted.get(row.id);
      /*
       * Підтверджене не розпідтверджуємо. Штатно цього не буває (сервер
       * ідемпотентний і на повторі віддає збережений результат), але якщо
       * колись трапиться — краще лишити дитині її гроші, ніж перевести
       * оплачену відповідь у «не зараховано».
       */
      if (!result && row.status === 'synced') continue;
      if (result) {
        counts.accepted += 1;
        updated.push({
          ...row,
          status: 'synced',
          syncedAt: now,
          lastError: null,
          rejection: null,
          result,
        });
        continue;
      }

      const rejection = refused.get(row.id);
      if (rejection) {
        /*
         * Стеля стоїть на ОКРЕМОМУ лічильнику, а не на `tries`. `tries` рахує
         * усі невдачі підряд, зокрема мережеві (`markFailed`), — і на ньому
         * стеля означала б, що після кількох хвилин лежачого сервера перший же
         * оборотний вирок згорає в `rejected`, жодного разу не повторившись.
         * Тут же ми питаємо саме «скільки разів сервер сказав „пізніше“».
         */
        const verdicts = (row.verdicts ?? 0) + (rejection.retryable ? 1 : 0);
        const tries = rejection.retryable ? row.tries + 1 : row.tries;
        const giveUp = !rejection.retryable || verdicts >= MAX_RETRYABLE_VERDICTS;
        if (giveUp) counts.rejected += 1;
        else counts.requeued += 1;
        const retryAt = rejection.retryAt ? new Date(rejection.retryAt).getTime() : NaN;
        updated.push({
          ...row,
          status: giveUp ? 'rejected' : 'pending',
          tries,
          verdicts,
          // Сервер сам каже, коли повертатись (розклад відчиниться о 16:00) —
          // це чесніше за нашу експоненційну затримку навмання.
          nextAttemptAt: giveUp
            ? row.nextAttemptAt
            : Math.max(now, Number.isFinite(retryAt) ? retryAt : now + computeBackoffMs(tries, random)),
          lastError: rejection.message,
          rejection,
          result: null,
        });
        continue;
      }

      /*
       * Сервер промовчав про цей рядок. Такого не має бути (контракт вимагає
       * вирок на кожну спробу), але мовчання ми не зараховуємо: рядок
       * повертається в чергу із затримкою, а не зникає в «синхронізовано
       * без результату».
       */
      const tries = row.tries + 1;
      counts.requeued += 1;
      updated.push({
        ...row,
        status: 'pending',
        tries,
        nextAttemptAt: now + computeBackoffMs(tries, random),
        lastError: 'Сервер не сказав про цю відповідь нічого',
      });
    }

    if (updated.length) await db.attempts.bulkPut(updated);
  });

  return counts;
}

export interface MarkFailedArgs {
  db: LearnKidsDb;
  ids: string[];
  error: string;
  /**
   * true — самим повторювати марно (валідація, конфлікт). Рядок лишається
   * видимим у UI і оборотним: `reviveRejected` поверне його в чергу.
   */
  permanent?: boolean;
  /**
   * false — не рахувати цю невдачу як спробу.
   * Так робимо на 401: винен не запит, а протухла сесія, тож нарощувати
   * затримку нечесно — після повторного входу треба відправити одразу.
   */
  bumpTries?: boolean;
  /** Явна затримка замість розрахованої. */
  delayMs?: number;
  now?: number;
  random?: () => number;
}

/**
 * Невдача. Головне тут: рядок НІКОЛИ не видаляється.
 * Мережева помилка → назад у `pending` з більшою затримкою.
 * Відмова сервера → `rejected`, видно в індикаторі й у кабінеті,
 * але й звідти є дорога назад (див. `reviveRejected`).
 */
export async function markFailed({
  db,
  ids,
  error,
  permanent = false,
  bumpTries = true,
  delayMs,
  now = Date.now(),
  random = Math.random,
}: MarkFailedArgs): Promise<void> {
  await db.transaction('rw', db.attempts, async () => {
    const rows = await db.attempts.bulkGet(ids);
    const updated: QueuedAttempt[] = [];
    for (const row of rows) {
      if (!row) continue;
      if (row.status === 'synced') continue;
      const tries = bumpTries ? row.tries + 1 : row.tries;
      const wait = delayMs ?? computeBackoffMs(Math.max(1, tries), random);
      updated.push({
        ...row,
        status: permanent ? 'rejected' : 'pending',
        tries,
        nextAttemptAt: permanent ? row.nextAttemptAt : now + wait,
        lastError: error,
      });
    }
    if (updated.length) await db.attempts.bulkPut(updated);
  });
}

/**
 * Повертає в чергу все, що зависло в `syncing`, і заразом лікує рядки,
 * закинуті в недосяжне майбутнє переведеним годинником.
 * Викликається на старті: якщо вкладку закрили посеред відправки,
 * ці рядки більше ніхто не зрушить — а вони містять відповіді дитини.
 */
export async function reviveStuck(db: LearnKidsDb, now = Date.now()): Promise<number> {
  return db.transaction('rw', db.attempts, async () => {
    const stuck = await db.attempts.where('status').equals('syncing').toArray();
    if (stuck.length > 0) {
      await db.attempts.bulkPut(
        stuck.map((row) => ({ ...row, status: 'pending' as const, nextAttemptAt: now })),
      );
    }
    return stuck.length + (await healFutureSchedules(db, now));
  });
}

/**
 * Чи цю паузу призначив САМ СЕРВЕР своїм `retryAt`.
 *
 * Розпізнаємо за точним збігом: `applyOutcomes` кладе `nextAttemptAt` рівним
 * названому серверу моменту, тож будь-яка наша пізніша затримка (`markFailed`)
 * цю рівність розіб'є — і рядок знову стане звичайним.
 *
 * Потрібно рівно для того, щоб лікування «майбутнього» не з'їдало чесну
 * серверну паузу: розклад відчиняється о 08:00, тобто за десять годин, а наш
 * горизонт — година. Без цієї перевірки рядок «дозрівав» за 15 секунд, знову
 * отримував той самий вирок — і за пʼять тактів згорав у `rejected`, хоча
 * сервер прямо сказав, коли його приймуть.
 */
function parkedByServer(row: QueuedAttempt, now: number): boolean {
  const retryAt = row.rejection?.retryable ? row.rejection.retryAt : null;
  if (!retryAt) return false;
  const at = new Date(retryAt).getTime();
  return at === row.nextAttemptAt && at - now <= MAX_SERVER_PARK_MS;
}

/**
 * Годинник пристрою перевели вперед, спроба отримала `nextAttemptAt` десь
 * у наступному році — і черга чекатиме на неї вічно. Повертаємо такі рядки
 * у «зараз»: краще зайва спроба, ніж відповідь, яка не поїде ніколи.
 */
export async function healFutureSchedules(db: LearnKidsDb, now = Date.now()): Promise<number> {
  return db.transaction('rw', db.attempts, async () => {
    const beyond = (
      await db.attempts
        .where('[status+nextAttemptAt]')
        .between(['pending', now + MAX_SCHEDULE_HORIZON_MS], ['pending', Dexie.maxKey], false, true)
        .toArray()
    ).filter((row) => !parkedByServer(row, now));
    if (beyond.length === 0) return 0;
    await db.attempts.bulkPut(beyond.map((row) => ({ ...row, nextAttemptAt: now })));
    return beyond.length;
  });
}

export interface ReviveRejectedOptions {
  /** Обмежити однією дитиною. Без цього оживають усі відхилені рядки. */
  learnerId?: string;
  now?: number;
}

/**
 * Дає `rejected` другий шанс. Стан «сервер відхилив» не має бути вироком:
 * найчастіша його причина — пачку відправили не під тією сесією, і після
 * входу правильної дитини вона пройде без жодних змін у даних.
 */
export async function reviveRejected(
  db: LearnKidsDb,
  options: ReviveRejectedOptions = {},
): Promise<number> {
  const now = options.now ?? Date.now();
  return db.transaction('rw', db.attempts, async () => {
    const rejected = await db.attempts.where('status').equals('rejected').toArray();
    const mine = options.learnerId
      ? rejected.filter((row) => row.learnerId === options.learnerId)
      : rejected;
    if (mine.length === 0) return 0;
    await db.attempts.bulkPut(
      // tries НЕ скидаємо: історія невдач лишається видимою, а затримку
      // все одно перерахує наступна помилка.
      mine.map((row) => ({ ...row, status: 'pending' as const, nextAttemptAt: now })),
    );
    return mine.length;
  });
}

export interface QueueCounts {
  pending: number;
  /** Скільки рядків прямо зараз у польоті. */
  inFlight: number;
  synced: number;
  rejected: number;
  /** Скільки відповідей ще не доїхало до сервера. Саме це число бачить дитина. */
  unsent: number;
}

/**
 * Скільки відповідей чекає відправки.
 *
 * `learnerId` тут не косметика. На спільному планшеті в базі лежать рядки
 * обох дітей, і без фільтра Богдан бачив би в шапці «3 не надіслано», що
 * належать Маркусі, — числа, яке він не може змінити ніякими діями. Тому
 * рахуємо тільки активного власника черги; `null` (батько в кабінеті)
 * означає, що показувати нічого.
 */
export async function countQueue(
  db: LearnKidsDb,
  learnerId?: string | null,
): Promise<QueueCounts> {
  const mine = (status: QueuedAttempt['status']) =>
    learnerId === undefined
      ? db.attempts.where('status').equals(status).count()
      : learnerId === null
        ? Promise.resolve(0)
        : db.attempts
            .where('status')
            .equals(status)
            .filter((row) => row.learnerId === learnerId)
            .count();

  const [pending, inFlight, synced, rejected] = await Promise.all([
    mine('pending'),
    mine('syncing'),
    mine('synced'),
    mine('rejected'),
  ]);
  return {
    pending,
    inFlight,
    synced,
    rejected,
    // «Не надіслано» — це ВСЕ, що сервер не підтвердив, разом з відхиленим.
    // Відхилене теж не доїхало, а стан цей оборотний; показувати нуль,
    // поки відповіді лежать на пристрої, — це брехати дитині.
    unsent: pending + inFlight + rejected,
  };
}

export function attemptsForSession(db: LearnKidsDb, sessionId: string): Promise<QueuedAttempt[]> {
  return db.attempts.where('sessionId').equals(sessionId).toArray();
}

/**
 * Прибирання: підтверджені спроби старші за тиждень уже не потрібні —
 * авторитетна історія живе на сервері. Нічого «непідтвердженого» не чіпаємо.
 */
export async function pruneSynced(db: LearnKidsDb, olderThanMs = 7 * 24 * 3600_000): Promise<number> {
  const cutoff = Date.now() - olderThanMs;
  const stale = await db.attempts.where('status').equals('synced').toArray();
  const ids = stale.filter((r) => (r.syncedAt ?? r.createdAt) < cutoff).map((r) => r.id);
  if (ids.length) await db.attempts.bulkDelete(ids);
  return ids.length;
}
